import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import type { MailParticipantsParameters } from '../../../../../../typings/api/skymap/rest/v0/project';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { ProjectContext } from '../../../state/project-state/project-state';
import { useToaster } from '../../../state/toaster-state';
import { Button } from '../../button/button';
import { InfoBox } from '../../info-box/info-box';
import { LabelledContainer } from '../../labelled-container/labelled-container';
import { Stack } from '../../stack/stack';
import { TextArea } from '../../text-area/text-area';
import { TextBox } from '../../text-box/text-box';

type SendProjectEmailVariables = {
  projectId: string;
  subject: string;
  body: string;
};

const EMAIL_FROM_ADDRESS = 'info@skymap.se';

const sendProjectEmail = async (variables: SendProjectEmailVariables) => {
  const service = SkyMapAxiosServiceFactory.instance.createProjectServiceV0();
  const params: MailParticipantsParameters = {
    body: {
      projectId: variables.projectId,
      subject: variables.subject,
      body: variables.body,
    },
  };

  await service.mailParticipants(params);
};

const ProjectEmail = () => {
  const { project } = React.useContext(ProjectContext);
  const { t } = useTranslation();
  const { showToast } = useToaster();
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [subjectError, setSubjectError] = React.useState<string | undefined>();
  const [messageError, setMessageError] = React.useState<string | undefined>();

  const defaultSubject = React.useMemo(() => {
    return project?.name ? `SkyMap: ${project.name}` : '';
  }, [project?.name]);

  const validationMessages = React.useMemo(
    () => ({
      subjectRequired: t('projectsEmail.validation.subjectRequired', { ns: 'components' }),
      bodyRequired: t('projectsEmail.validation.bodyRequired', { ns: 'components' }),
    }),
    [t],
  );

  React.useEffect(() => {
    setSubject(defaultSubject);
    setMessage('');
    setSubjectError(undefined);
    setMessageError(undefined);
  }, [defaultSubject]);

  const mutation = useMutation({
    mutationFn: sendProjectEmail,
    onSuccess: () => {
      showToast({
        type: 'success',
        title: t('toaster.title.success', { ns: 'common' }),
        message: t('projectsEmail.toaster.success', { ns: 'components' }),
      });
      setMessage('');
      setSubject(defaultSubject);
      setSubjectError(undefined);
      setMessageError(undefined);
    },
    onError: (error: unknown) => {
      const code =
        (error as { response?: { data?: { code?: string } } })?.response?.data?.code ?? undefined;
      showToast({
        type: 'error',
        title: t('toaster.title.error', { ns: 'common' }),
        message:
          code === 'no_users'
            ? t('projectsEmail.toaster.noRecipients', { ns: 'components' })
            : t('projectsEmail.toaster.genericError', { ns: 'components' }),
      });
    },
  });

  if (!project) {
    return null;
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedSubject = subject.trim();
    const trimmedBody = message.trim();

    let hasError = false;

    if (!trimmedSubject) {
      setSubjectError(validationMessages.subjectRequired);
      showToast({
        type: 'error',
        title: t('toaster.title.error', { ns: 'common' }),
        message: validationMessages.subjectRequired,
      });
      hasError = true;
    } else {
      setSubjectError(undefined);
    }

    if (!trimmedBody) {
      setMessageError(validationMessages.bodyRequired);
      showToast({
        type: 'error',
        title: t('toaster.title.error', { ns: 'common' }),
        message: validationMessages.bodyRequired,
      });
      hasError = true;
    } else {
      setMessageError(undefined);
    }

    if (hasError) {
      return;
    }

    mutation.mutate({
      projectId: project.id,
      subject: trimmedSubject,
      body: trimmedBody,
    });
  };

  return (
    <Container spacing={2.5}>
      <InfoBox color="yellow">{t('projectsEmail.info', { ns: 'components' })}</InfoBox>

      <Form onSubmit={handleSubmit}>
        <Stack spacing={1.5}>
          <LabelledContainer text={t('projectsEmail.form.from', { ns: 'components' })}>
            {(id) => (
              <TextBox
                disabled={true}
                id={id()}
                readOnly={true}
                readOnlyCursor="text"
                value={EMAIL_FROM_ADDRESS}
              />
            )}
          </LabelledContainer>

          <LabelledContainer
            required={true}
            text={t('projectsEmail.form.subject', { ns: 'components' })}
          >
            {(id) => (
              <TextBox
                errorMessage={subjectError}
                id={id()}
                maxLength={255}
                value={subject}
                onChange={(event) => setSubject(event.currentTarget.value)}
              />
            )}
          </LabelledContainer>

          <LabelledContainer
            required={true}
            text={t('projectsEmail.form.body', { ns: 'components' })}
          >
            {(id) => (
              <TextArea
                errorMessage={messageError}
                id={id()}
                rows={10}
                value={message}
                onChange={(event) => setMessage(event.currentTarget.value)}
              />
            )}
          </LabelledContainer>
        </Stack>

        <Actions>
          <Button
            color="primary"
            disabled={mutation.status === 'pending'}
            leftIcon={{ icon: mutation.status === 'pending' ? ['fas', 'spinner'] : 'paper-plane' }}
            loading={mutation.status === 'pending'}
            type="submit"
            variant="contained"
          >
            {t('projectsEmail.form.submit', { ns: 'components' })}
          </Button>
        </Actions>
      </Form>
    </Container>
  );
};

const Container = styled(Stack)`
  width: 100%;
  align-items: stretch;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  align-self: flex-end;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export { ProjectEmail };
